import React, { useState } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Reusable confirm dialog with required reason for destructive admin actions.
 * Caller controls open state and supplies onConfirm(reason). Dialog blocks
 * confirm until reason is entered (when requireReason).
 */
export default function AdminConfirmDialog({
    open,
    onClose,
    onConfirm,
    title,
    description,
    confirmLabel = "Confirm",
    danger = false,
    requireReason = true,
    initialReason = "",
}) {
    const [reason, setReason] = useState(initialReason);
    const [busy, setBusy] = useState(false);

    React.useEffect(() => {
        if (open) setReason(initialReason);
    }, [open, initialReason]);

    if (!open) return null;
    const canSubmit = !requireReason || reason.trim().length >= 3;

    const submit = async () => {
        if (!canSubmit || busy) return;
        setBusy(true);
        try {
            await onConfirm(reason.trim());
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="admin-confirm-dialog"
            className="fixed inset-0 z-50 flex items-center justify-center bg-midnight-pine/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="bg-snowcap rounded-lg shadow-2xl border border-ridge-line w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between p-5 border-b border-ridge-line">
                    <div className="flex items-start gap-3">
                        {danger && <AlertTriangle className="w-5 h-5 text-ember-orange shrink-0 mt-0.5" />}
                        <div>
                            <h3 className="font-display font-bold text-lg text-midnight-pine leading-tight">
                                {title}
                            </h3>
                            {description && (
                                <p className="text-sm text-slate-gray mt-1 leading-relaxed">
                                    {description}
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        data-testid="admin-confirm-close"
                        onClick={onClose}
                        className="text-slate-gray hover:text-granite p-1"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    {requireReason && (
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-gray mb-2">
                                Reason (required, min 3 chars)
                            </label>
                            <textarea
                                data-testid="admin-confirm-reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                rows={3}
                                placeholder="e.g. Repeated spam reports from multiple users"
                                className="w-full px-3 py-2 rounded-md bg-alpine-mist border border-ridge-line text-sm focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                                autoFocus
                            />
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 p-5 border-t border-ridge-line bg-alpine-mist/30">
                    <button
                        data-testid="admin-confirm-cancel"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md text-sm font-bold text-granite hover:bg-alpine-mist transition-colors"
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="admin-confirm-submit"
                        onClick={submit}
                        disabled={!canSubmit || busy}
                        className={`px-4 py-2 rounded-md text-sm font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            danger ? "bg-ember-orange hover:bg-ember-dark" : "bg-midnight-pine hover:bg-deep-evergreen"
                        }`}
                    >
                        {busy ? "Working…" : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
